import React, { useEffect, useState, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
  Box, Card, CardContent, Typography, TextField, Switch, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Select, MenuItem, FormControl, InputLabel,
  CircularProgress, Tooltip, FormControlLabel, Tabs, Tab, Divider,
  ToggleButtonGroup, ToggleButton, LinearProgress, Accordion, AccordionSummary,
  AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { Save, Delete, Add, Refresh, ExpandMore, History } from '@mui/icons-material';
import { cleanCustomerId } from '../../Services/app/operatorservice';
import axiosInstance1 from '../../Services/app/loginservice';

// Machine Interlock Settings — Phase 1: UI + storage only, no enforcement.
// Writes a per-DEVICE server-scope attribute `interlock_config`:
//   { params: { <key>: { enabled, min, max, kind, mode } },
//     jidoka: {...},
//     updated_by, updated_at }
const ATTR_KEY = 'interlock_config';
const ALARM_PATTERN = /alarm|warn|fault|error/i;
const isAlarmKey = (key) => ALARM_PATTERN.test(key || '');

const HIDDEN_KEYS = new Set(['ts', 'timestamp', 'machine_id', 'device_id']);

const isNumericInterlockable = (v) => {
  if (v === null || v === undefined || v === '') return false;
  if (typeof v === 'object') return false;
  const s = String(v).trim();
  if (s.startsWith('{') || s.startsWith('[')) return false;
  if (/[a-zA-Z]/.test(s) && !/^-?\d+(\.\d+)?(e[+-]?\d+)?$/i.test(s)) return false;
  const n = Number(s);
  if (isNaN(n) || !isFinite(n)) return false;
  if (Math.abs(n) > 1e11) return false; // probably epoch ms
  return true;
};

const fmt = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'object') {
    const s = JSON.stringify(v);
    return s.length > 60 ? s.slice(0, 60) + '…' : s;
  }
  const n = Number(v);
  if (!isNaN(n) && isFinite(n) && !/[a-zA-Z]/.test(String(v))) {
    if (Number.isInteger(n)) return n;
    return n.toFixed(2);
  }
  const s = String(v);
  return s.length > 60 ? s.slice(0, 60) + '…' : s;
};

// Decide initial alarm mode from saved entry shape
const deriveAlarmMode = (entry) => {
  if (!entry) return 'present';
  if (entry.mode) return entry.mode;
  const hasNum = (entry.min !== null && entry.min !== '' && !isNaN(Number(entry.min)))
              || (entry.max !== null && entry.max !== '' && !isNaN(Number(entry.max)));
  if (hasNum) return 'numeric_range';
  if (entry.min && typeof entry.min === 'string') return 'match_text';
  return 'present';
};

const JIDOKA_CAUSES_DEFAULT = ['Tool wear', 'Material', 'Operator', 'Machine fault', 'Setup', 'Other'];
const JIDOKA_DEFAULT = {
  enabled: false,
  stop_on_consecutive_bad_parts: 3,
  cycle_time_deviation_pct: 25,
  idle_alert_seconds: 300,
  andon_cord_enabled: true,
  require_supervisor_ack: true,
  root_cause_categories: JIDOKA_CAUSES_DEFAULT,
  patterns: [],
};

// Pattern types — orchestrate enabled numeric + alarm interlocks into smarter halts
const PATTERN_TYPES = [
  {
    value: 'repeated_alarm',
    label: 'Repeated alarm',
    desc: 'Same alarm fires N times in window',
    fields: ['min_count', 'window_minutes', 'alarm_filter'],
    default: { window_minutes: 15, min_count: 3, alarm_filter: 'any' },
  },
  {
    value: 'alarm_cluster',
    label: 'Alarm cluster',
    desc: 'Any N enabled alarms in window (regardless of type)',
    fields: ['min_count', 'window_minutes'],
    default: { window_minutes: 60, min_count: 5 },
  },
  {
    value: 'combined',
    label: 'Combined breach',
    desc: 'Alarm active AND numeric breach in same window',
    fields: ['window_minutes'],
    default: { window_minutes: 1 },
  },
  {
    value: 'sequence',
    label: 'Sequence',
    desc: 'Alarm A fires, then Alarm B within window',
    fields: ['window_minutes', 'first_alarm', 'second_alarm'],
    default: { window_minutes: 5, first_alarm: '', second_alarm: '' },
  },
  {
    value: 'cumulative_shift',
    label: 'Shift threshold',
    desc: 'Total interlock fires in the current shift exceeds N',
    fields: ['min_count'],
    default: { min_count: 10 },
  },
];
// Tool life — per-tool expected lifespan + warning/halt thresholds
const TOOL_LIFE_DEFAULT = {
  enabled: false,
  tools: [],
};
const newTool = (nextNumber) => ({
  tool_number: nextNumber,
  name: '',
  life_minutes: 180,
  life_parts: 500,
  warning_pct: 80,
  halt_pct: 100,
  halt_machine: true,
  enabled: true,
  wear_curve: {
    enabled: false,
    rate_um_per_100: 5,    // micrometers of wear per 100 parts
    axis: 'radial',         // 'radial' (diameter) or 'axial' (length)
    notes: '',
  },
});

const newPattern = (type) => {
  const def = PATTERN_TYPES.find(t => t.value === type) || PATTERN_TYPES[0];
  return {
    name: def.label,
    type: def.value,
    enabled: true,
    ...def.default,
    action: { halt: true, require_supervisor_ack: true },
  };
};

const InterlockSettings = () => {
  const customerId = localStorage.getItem('CustomerID');
  const userName = localStorage.getItem('username') || localStorage.getItem('user') || '';
  const authority = (() => {
    try { return JSON.parse(localStorage.getItem('authority')) || ''; }
    catch { return localStorage.getItem('authority') || ''; }
  })();
  const userRoleRaw = (() => {
    try { return JSON.parse(localStorage.getItem('userDetails')) || {}; }
    catch { return {}; }
  })();
  const normalize = (s) => String(s || '').toLowerCase().replace(/[\s_-]+/g, '');
  const roleCandidates = [
    userRoleRaw?.mode, userRoleRaw?.role, userRoleRaw?.userRole, userRoleRaw?.Role,
    userRoleRaw?.description, userRoleRaw?.title,
    localStorage.getItem('role_name'),
  ].map(normalize);
  const isAuthorized =
    authority === 'TENANT_ADMIN' ||
    authority === 'SYS_ADMIN' ||
    roleCandidates.some(v => ['superadmin', 'admin', 'sysadmin', 'tenantadmin'].includes(v));

  const SERVER = (window._env_?.SERVER_URL || '').replace(/\/$/, '');
  const cid = cleanCustomerId(customerId);

  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [params, setParams] = useState([]);
  const [liveValues, setLiveValues] = useState({});
  const [discoveredKeys, setDiscoveredKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [filter, setFilter] = useState('');
  const [customKey, setCustomKey] = useState('');
  const [tab, setTab] = useState(0); // 0 = Numeric, 1 = Alarms, 2 = Jidoka

  const [jidoka, setJidoka] = useState(JIDOKA_DEFAULT);
  const [jidokaCauseDraft, setJidokaCauseDraft] = useState('');
  const [toolLife, setToolLife] = useState(TOOL_LIFE_DEFAULT);
  // Wear-curve dialog: which tool index is being edited, plus a working copy of its wear_curve
  const [wearDialogIdx, setWearDialogIdx] = useState(null);
  const [wearDraft, setWearDraft] = useState(null);
  // Live tool data (edge-published): { "1": { health_pct, minutes, parts, last_reset_at }, ... }
  const [toolLiveData, setToolLiveData] = useState({});
  // Tool change history events from telemetry
  const [toolHistory, setToolHistory] = useState([]);
  const [loadingToolLive, setLoadingToolLive] = useState(false);
  const [resettingTool, setResettingTool] = useState(null);

  // Observed alarms: distinct (alarm_number, alarm_message) seen on this machine in last 7 days.
  // Saved as interlock_config.alarm_rules → array of { number, message, enabled, halt }.
  const [observedAlarms, setObservedAlarms] = useState([]);
  const [loadingAlarms, setLoadingAlarms] = useState(false);
  const [alarmHistoryDays, setAlarmHistoryDays] = useState(7);
  const [alarmSearch, setAlarmSearch] = useState('');

  const selectedDevice = useMemo(
    () => devices.find(d => d.id?.id === selectedId),
    [devices, selectedId]
  );

  // Load devices
  useEffect(() => {
    if (!isAuthorized || !cid) return;
    setLoadingDevices(true);
    axiosInstance1
      .get(`${SERVER}/api/customer/${cid}/devices`, { params: { pageSize: 500, page: 0 } })
      .then(r => {
        const list = r.data?.data || [];
        list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setDevices(list);
      })
      .catch(e => Swal.fire('Error', 'Failed to load machine list: ' + (e?.message || ''), 'error'))
      .finally(() => setLoadingDevices(false));
  }, [SERVER, cid, isAuthorized]);

  // On device select: load existing config + discover keys + live values
  useEffect(() => {
    if (!selectedId) {
      setParams([]); setLiveValues({}); setDiscoveredKeys([]); setLastSaved(null);
      setJidoka(JIDOKA_DEFAULT);
      setObservedAlarms([]);
      setToolLife(TOOL_LIFE_DEFAULT);
      setToolLiveData({});
      setToolHistory([]);
      return;
    }
    setLoading(true);
    (async () => {
      try {
        const cfgResp = await axiosInstance1.get(
          `${SERVER}/api/plugins/telemetry/DEVICE/${selectedId}/values/attributes`,
          { params: { keys: ATTR_KEY } }
        );
        const cfgAttr = (cfgResp.data || []).find(a => a.key === ATTR_KEY);
        const existing = (cfgAttr?.value && typeof cfgAttr.value === 'object') ? cfgAttr.value : { params: {} };
        setLastSaved(cfgAttr?.lastUpdateTs ? { at: cfgAttr.lastUpdateTs, by: existing.updated_by } : null);
        setJidoka({ ...JIDOKA_DEFAULT, ...(existing.jidoka || {}) });
        const tl = { ...TOOL_LIFE_DEFAULT, ...(existing.tool_life || {}) };
        setToolLife(tl);
        // Live tool data + history, parallel and non-blocking
        loadToolLiveData(selectedId, tl.tools || []);
        loadToolHistory(selectedId);

        const keysResp = await axiosInstance1.get(
          `${SERVER}/api/plugins/telemetry/DEVICE/${selectedId}/keys/timeseries`
        );
        const allKeys = (keysResp.data || []).filter(k => !HIDDEN_KEYS.has(k));
        setDiscoveredKeys(allKeys);

        let live = {};
        if (allKeys.length > 0) {
          const valResp = await axiosInstance1.get(
            `${SERVER}/api/plugins/telemetry/DEVICE/${selectedId}/values/timeseries`,
            { params: { keys: allKeys.join(','), useStrictDataTypes: false } }
          );
          Object.entries(valResp.data || {}).forEach(([k, arr]) => {
            if (arr?.length) live[k] = arr[0].value;
          });
        }
        setLiveValues(live);

        const existingParams = existing.params || {};
        const allParamKeys = Array.from(new Set([...Object.keys(existingParams), ...allKeys]));
        const merged = allParamKeys.map(k => {
          const e = existingParams[k] || {};
          return {
            key: k,
            enabled: e.enabled ?? false,
            min: e.min ?? '',
            max: e.max ?? '',
            // Numeric-only "sustain" condition: breach must hold for `duration` seconds before notifying.
            sustain: e.sustain ?? false,
            duration: e.sustain_seconds ?? '',
            custom: !!existingParams[k] && !allKeys.includes(k),
            alarmMode: isAlarmKey(k) ? deriveAlarmMode(e) : null,
          };
        });
        setParams(merged);

        // Fetch distinct historical alarms in parallel (don't block on this)
        loadObservedAlarms(selectedId, existing.alarm_rules || []);
      } catch (e) {
        Swal.fire('Error', 'Failed to load machine settings: ' + (e?.response?.data?.message || e?.message || ''), 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedId, SERVER]);

  // Re-fetch alarms when day-window changes
  useEffect(() => {
    if (selectedId) {
      // Pull saved rules first so toggles stick across reloads
      axiosInstance1.get(`${SERVER}/api/plugins/telemetry/DEVICE/${selectedId}/values/attributes`, { params: { keys: ATTR_KEY } })
        .then(r => {
          const cfg = (r.data || []).find(a => a.key === ATTR_KEY)?.value || {};
          loadObservedAlarms(selectedId, cfg.alarm_rules || []);
        })
        .catch(() => loadObservedAlarms(selectedId, []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alarmHistoryDays]);

  const loadObservedAlarms = async (deviceId, savedRules) => {
    setLoadingAlarms(true);
    try {
      const endTs = Date.now();
      const startTs = endTs - alarmHistoryDays * 24 * 3600 * 1000;
      // Fetch alarm_number + alarm_message history in one call
      const r = await axiosInstance1.get(
        `${SERVER}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries`,
        { params: { keys: 'alarm_number,alarm_message,live_alarm', startTs, endTs, limit: 5000, useStrictDataTypes: false } }
      );
      const data = r.data || {};
      // Dedupe by alarm_number + message together: same number+message collapses into one
      // row (with a Times-Seen count), but the same message under different alarm numbers
      // stays as distinct rows. Key format matches the saved-rule merge/match below.
      const map = new Map();
      const addEvent = (number, message, ts, extra) => {
        const msg = (message || '').trim();
        if (!msg && !number) return;
        const key = `${number ?? ''}|${msg}`;
        const prev = map.get(key);
        if (prev) {
          prev.count += 1;
          if (ts > prev.lastSeen) prev.lastSeen = ts;
          if (number && !prev.number) prev.number = number;
          if (extra) Object.assign(prev, extra);
        } else {
          map.set(key, { number: number || null, message: msg || null, count: 1, lastSeen: ts, ...(extra || {}) });
        }
      };
      // PRIMARY source: live_alarm JSON (richest — has number, message, type, duration in one event)
      const liveAlarmMsgs = new Set(); // messages already captured here, to suppress redundant alarm_message rows
      (data.live_alarm || []).forEach(e => {
        try {
          const obj = typeof e.value === 'string' ? JSON.parse(e.value) : e.value;
          if (!obj) return;
          // alarm_number=0 typically means "alarm cleared" — skip
          if (!obj.alarm_message && (!obj.alarm_number || obj.alarm_number === 0)) return;
          addEvent(obj.alarm_number ?? null, obj.alarm_message ?? null, e.ts, {
            alarm_type: obj.alarm_type,
            avg_duration: obj.alarm_duration,
          });
          const m = String(obj.alarm_message ?? '').trim();
          if (m) liveAlarmMsgs.add(m);
        } catch {}
      });
      // FALLBACK: alarm_message timeseries (for machines that don't publish live_alarm JSON).
      // Skip messages already captured from live_alarm — the machine publishes the same alarm
      // on both streams, and the live_alarm row already carries the alarm number.
      (data.alarm_message || []).forEach(e => {
        const msg = String(e.value || '').trim();
        if (msg && !liveAlarmMsgs.has(msg)) addEvent(null, msg, e.ts);
      });
      // Merge with saved rules so toggles persist even if alarm hasn't recurred lately
      const rules = Array.isArray(savedRules) ? savedRules : [];
      rules.forEach(r => {
        const key = `${r.number ?? ''}|${r.message ?? ''}`;
        if (!map.has(key)) {
          map.set(key, { number: r.number, message: r.message, count: 0, lastSeen: 0 });
        }
      });
      const list = Array.from(map.values()).map(a => {
        const saved = rules.find(r => (r.number ?? null) === (a.number ?? null) && (r.message ?? null) === (a.message ?? null));
        return {
          ...a,
          enabled: !!saved?.enabled,
          halt: saved?.halt ?? true,
        };
      });
      // Sort: enabled first, then by count desc, then by lastSeen desc
      list.sort((a, b) => {
        if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
        if (b.count !== a.count) return b.count - a.count;
        return b.lastSeen - a.lastSeen;
      });
      setObservedAlarms(list);
    } catch (e) {
      console.error('Failed to load alarm history:', e);
      setObservedAlarms([]);
    } finally {
      setLoadingAlarms(false);
    }
  };

  const toggleObservedAlarm = (idx, field, value) => {
    setObservedAlarms(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  // Fetch live per-tool telemetry (edge publishes tool_T<n>_health_pct, _minutes, _parts)
  const loadToolLiveData = async (deviceId, configuredTools) => {
    if (!deviceId || !configuredTools || configuredTools.length === 0) {
      setToolLiveData({});
      return;
    }
    setLoadingToolLive(true);
    try {
      const keys = [];
      configuredTools.forEach(t => {
        const n = String(t.tool_number).padStart(2, '0');
        keys.push(`tool_T${n}_health_pct`, `tool_T${n}_minutes`, `tool_T${n}_parts`, `tool_T${n}_last_reset_at`);
      });
      const resp = await axiosInstance1.get(
        `${SERVER}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries`,
        { params: { keys: keys.join(','), useStrictDataTypes: false } }
      );
      const data = resp.data || {};
      const byTool = {};
      configuredTools.forEach(t => {
        const n = String(t.tool_number).padStart(2, '0');
        byTool[t.tool_number] = {
          health_pct: data[`tool_T${n}_health_pct`]?.[0]?.value,
          minutes: data[`tool_T${n}_minutes`]?.[0]?.value,
          parts: data[`tool_T${n}_parts`]?.[0]?.value,
          last_reset_at: data[`tool_T${n}_last_reset_at`]?.[0]?.value,
        };
      });
      setToolLiveData(byTool);
    } catch (e) {
      console.error('Tool live fetch failed:', e);
      setToolLiveData({});
    } finally {
      setLoadingToolLive(false);
    }
  };

  // Fetch tool change history (last 90 days of `tool_change_log` telemetry)
  const loadToolHistory = async (deviceId) => {
    if (!deviceId) { setToolHistory([]); return; }
    try {
      const endTs = Date.now();
      const startTs = endTs - 90 * 24 * 3600 * 1000;
      const resp = await axiosInstance1.get(
        `${SERVER}/api/plugins/telemetry/DEVICE/${deviceId}/values/timeseries`,
        { params: { keys: 'tool_change_log', startTs, endTs, limit: 500, useStrictDataTypes: false } }
      );
      const raw = (resp.data?.tool_change_log || []);
      const events = raw.map(e => {
        try {
          const obj = typeof e.value === 'string' ? JSON.parse(e.value) : e.value;
          return { ts: e.ts, ...obj };
        } catch { return null; }
      }).filter(Boolean);
      events.sort((a, b) => b.ts - a.ts);
      setToolHistory(events);
    } catch (e) {
      console.error('Tool history fetch failed:', e);
      setToolHistory([]);
    }
  };

  // Reset a tool: post a SHARED attribute that edge picks up and zeros the counter
  const resetTool = async (tool) => {
    if (!selectedId || !tool) return;
    const ok = await Swal.fire({
      title: `Reset tool T${String(tool.tool_number).padStart(2, '0')}?`,
      html: `<div style="font-size:13px;text-align:left;color:#374151">
        <p><b>${tool.name || '(unnamed)'}</b></p>
        <p>This zeroes the cumulative cutting time and part count for this tool. Do this when the physical tool has been replaced.</p>
        <p style="color:#9ca3af;font-size:12px;margin-top:8px">An audit entry will be logged with your name + the previous usage.</p>
      </div>`,
      showCancelButton: true,
      confirmButtonText: 'Yes, reset',
      confirmButtonColor: '#1e40af',
    });
    if (!ok.isConfirmed) return;
    setResettingTool(tool.tool_number);
    try {
      const reset = {
        tool_reset_request: {
          tool_number: tool.tool_number,
          name: tool.name || '',
          by: userName,
          ts: Date.now(),
        },
      };
      await axiosInstance1.post(`${SERVER}/api/plugins/telemetry/DEVICE/${selectedId}/SHARED_SCOPE`, reset);
      Swal.fire({ title: 'Reset sent', text: `Tool T${tool.tool_number} reset has been pushed to the machine.`, icon: 'success', timer: 1700, showConfirmButton: false });
      // refresh live + history after a short delay so edge has a chance to confirm
      setTimeout(() => {
        loadToolLiveData(selectedId, toolLife.tools || []);
        loadToolHistory(selectedId);
      }, 1500);
    } catch (e) {
      Swal.fire('Reset failed', e?.response?.data?.message || e?.message || 'Unknown', 'error');
    } finally {
      setResettingTool(null);
    }
  };

  const updateParam = (key, field, value) => {
    setParams(prev => prev.map(p => {
      if (p.key !== key) return p;
      const next = { ...p, [field]: value };
      // If changing alarmMode, reset min/max to sensible defaults for new mode
      if (field === 'alarmMode') {
        if (value === 'present') { next.min = ''; next.max = ''; }
        else if (value === 'match_text') { next.max = ''; }
      }
      return next;
    }));
  };

  const addCustomParam = () => {
    const k = customKey.trim();
    if (!k) return;
    if (params.some(p => p.key === k)) {
      Swal.fire('Already exists', `Parameter "${k}" is already in the list.`, 'info');
      return;
    }
    setParams(prev => [{
      key: k, enabled: true, min: '', max: '', sustain: false, duration: '', custom: true,
      alarmMode: isAlarmKey(k) ? 'present' : null,
    }, ...prev]);
    setCustomKey('');
  };

  const removeParam = (key) => setParams(prev => prev.filter(p => p.key !== key));

  const save = async () => {
    if (!selectedId) return;
    const enabledParams = params.filter(p => p.enabled);
    const bad = enabledParams.filter(p => {
      if (isAlarmKey(p.key)) {
        if (p.alarmMode === 'present') return false;
        if (p.alarmMode === 'match_text') return !p.min || String(p.min).trim() === '';
        if (p.alarmMode === 'numeric_range') return p.min === '' && p.max === '';
      }
      return p.min === '' && p.max === '';
    });
    if (bad.length) {
      const ok = await Swal.fire({
        title: 'Some enabled rules are missing inputs',
        text: `${bad.map(p => p.key).join(', ')} — save anyway?`,
        showCancelButton: true,
        confirmButtonText: 'Yes, save',
      });
      if (!ok.isConfirmed) return;
    }
    setSaving(true);
    try {
      const payload = {};
      params.forEach(p => {
        if (!p.enabled) return;
        const isAlarm = isAlarmKey(p.key);
        const isNumMin = p.min !== '' && !isNaN(Number(p.min)) && isFinite(Number(p.min)) && !/[a-zA-Z]/.test(String(p.min));
        const isNumMax = p.max !== '' && !isNaN(Number(p.max)) && isFinite(Number(p.max)) && !/[a-zA-Z]/.test(String(p.max));
        const entry = {
          enabled: true,
          min: p.min === '' ? null : (isNumMin ? Number(p.min) : String(p.min)),
          max: p.max === '' ? null : (isNumMax ? Number(p.max) : String(p.max)),
        };
        if (isAlarm) {
          entry.kind = 'alarm';
          entry.mode = p.alarmMode || 'present';
        } else {
          // Numeric-only sustain condition: only notify after the breach holds this long.
          entry.sustain = !!p.sustain;
          entry.sustain_seconds = (p.sustain && p.duration !== '' && p.duration != null && !isNaN(Number(p.duration)))
            ? Number(p.duration)
            : null;
        }
        payload[p.key] = entry;
      });
      // Enabled observed-alarm rules (specific alarm_number/alarm_message)
      const alarm_rules = observedAlarms
        .filter(a => a.enabled)
        .map(a => ({ number: a.number, message: a.message, halt: a.halt }));

      const attr = {
        [ATTR_KEY]: {
          params: payload,
          jidoka,
          alarm_rules,
          tool_life: toolLife,
          updated_by: userName,
          updated_at: Date.now(),
        },
      };
      // Save to SERVER_SCOPE only (single source of truth for interlock_config).
      await axiosInstance1.post(`${SERVER}/api/plugins/telemetry/DEVICE/${selectedId}/SERVER_SCOPE`, attr);
      // Best-effort: remove any legacy duplicate left in SHARED_SCOPE so the key exists once.
      axiosInstance1
        .delete(`${SERVER}/api/plugins/telemetry/DEVICE/${selectedId}/SHARED_SCOPE`, { params: { keys: ATTR_KEY } })
        .catch(() => {});
      Swal.fire({ title: 'Saved', text: 'Settings applied to the machine.', icon: 'success', timer: 1700, showConfirmButton: false });
      setLastSaved({ at: Date.now(), by: userName });
    } catch (e) {
      Swal.fire('Save failed', e?.response?.data?.message || e?.message || 'Unknown', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthorized) {
    return (
      <Box sx={{ p: 5, textAlign: 'center' }}>
        <Typography variant="h4">🔒</Typography>
        <Typography variant="h6" sx={{ mt: 2 }}>Forbidden</Typography>
        <Typography sx={{ mt: 1, color: 'text.secondary' }}>
          Interlock Settings is restricted to admin users.
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 12, color: 'text.disabled' }}>
          Your role: <code>{authority || 'unknown'}</code>
        </Typography>
      </Box>
    );
  }

  // ---- derived lists per tab ----
  const numericParams = params.filter(p => !isAlarmKey(p.key) && (isNumericInterlockable(liveValues[p.key]) || p.custom));
  const alarmParams = params.filter(p => isAlarmKey(p.key));
  const filteredNumeric = numericParams.filter(p => !filter || p.key.toLowerCase().includes(filter.toLowerCase()));
  filteredNumeric.sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    return a.key.localeCompare(b.key);
  });
  const filteredAlarms = alarmParams.filter(p => !filter || p.key.toLowerCase().includes(filter.toLowerCase()));
  filteredAlarms.sort((a, b) => {
    if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
    return a.key.localeCompare(b.key);
  });

  const enabledNumericCount = numericParams.filter(p => p.enabled).length;
  // Alarms tab shows observedAlarms (distinct alarms fired by the machine), so its
  // count must come from observedAlarms — not alarmParams (alarm-named telemetry keys).
  const enabledAlarmCount = observedAlarms.filter(a => a.enabled).length;

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 80px)', overflow: 'auto', bgcolor: '#f8fafc' }}>
      {/* ── HEADER ──────────────────────────────────────────────── */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>🛡 Machine Interlock Configuration</Typography>
        <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
          Per-machine thresholds, alarm triggers, and Jidoka rules. Saved configuration is pushed live to the machine.
        </Typography>
      </Box>

      {/* ── TOP BAR: machine picker + stats + save ─────────────── */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'var(--y-bg, #fff)' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel>Machine</InputLabel>
            <Select
              value={selectedId}
              label="Machine"
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={loadingDevices}
            >
              {devices.map(d => (
                <MenuItem key={d.id?.id} value={d.id?.id}>{d.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedDevice && (
            <Box sx={{ display: 'flex', flexDirection: 'column', fontSize: 12, color: 'text.secondary' }}>
              <span><b>Type:</b> {selectedDevice.type || '—'} &nbsp; <b>Label:</b> {selectedDevice.label || '—'}</span>
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          {selectedId && !loading && (
            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip size="small" label={`${discoveredKeys.length} keys`} />
              <Chip size="small" variant="outlined" label={`${enabledNumericCount}/${numericParams.length} numeric`} />
              <Chip
                size="small"
                label={`🚨 ${enabledAlarmCount}/${observedAlarms.length} alarms`}
                sx={{ bgcolor: enabledAlarmCount ? '#fee2e2' : '#f3f4f6', color: enabledAlarmCount ? '#b91c1c' : '#374151', fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`自働化 Jidoka ${jidoka.enabled ? 'ON' : 'off'}`}
                sx={{ bgcolor: jidoka.enabled ? '#fecaca' : '#f3f4f6', color: jidoka.enabled ? '#991b1b' : '#374151', fontWeight: 600 }}
              />
              <Chip
                size="small"
                label={`🔧 ${toolLife.enabled ? `${(toolLife.tools || []).filter(t => t.enabled).length} tools` : 'tool life off'}`}
                sx={{ bgcolor: toolLife.enabled ? '#dbeafe' : '#f3f4f6', color: toolLife.enabled ? '#1e40af' : '#374151', fontWeight: 600 }}
              />
            </Box>
          )}

          <TextField
            size="small"
            placeholder="filter…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            sx={{ width: 140 }}
            disabled={!selectedId}
          />
          <Button
            variant="contained"
            color="primary"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            onClick={save}
            disabled={saving || !selectedId}
          >
            Save
          </Button>
        </Box>
        {lastSaved?.at && (
          <Box sx={{ mt: 1, fontSize: 11, color: 'text.secondary' }}>
            Last saved {new Date(lastSaved.at).toLocaleString()} by <b>{lastSaved.by || '—'}</b>
          </Box>
        )}
      </Paper>

      {/* ── EMPTY STATE ─────────────────────────────────────────── */}
      {!selectedId && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', color: 'text.secondary', bgcolor: 'var(--y-bg, #fff)' }}>
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>
            👆 Pick a machine above to begin
          </Typography>
          <Typography sx={{ fontSize: 13, mt: 1 }}>
            Auto-discovers every parameter the machine publishes. Three tabs: numeric thresholds, alarm triggers, Jidoka rules.
          </Typography>
        </Paper>
      )}

      {/* ── LOADING ─────────────────────────────────────────────── */}
      {selectedId && loading && (
        <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'var(--y-bg, #fff)' }}>
          <CircularProgress />
        </Paper>
      )}

      {/* ── TABS + CONTENT ──────────────────────────────────────── */}
      {selectedId && !loading && (
        <Paper variant="outlined" sx={{ bgcolor: 'var(--y-bg, #fff)' }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label={`📊 Numeric Thresholds (${numericParams.length})`} />
            <Tab label={`🚨 Alarms (${observedAlarms.length})`} sx={{ color: observedAlarms.length ? '#b91c1c' : undefined }} />
            <Tab label={`自働化 Jidoka`} sx={{ color: jidoka.enabled ? '#991b1b' : undefined }} />
            <Tab label={`🔧 Tool Life (${(toolLife.tools || []).length})`} sx={{ color: toolLife.enabled ? '#1e40af' : undefined }} />
          </Tabs>

          {/* ──────────── TAB 0: NUMERIC ──────────── */}
          {tab === 0 && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  label="+ Add custom param (e.g. coolant_pressure)"
                  value={customKey}
                  onChange={(e) => setCustomKey(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addCustomParam(); }}
                  sx={{ flex: 1 }}
                />
                <Button onClick={addCustomParam} variant="outlined" size="small" startIcon={<Add />}>Add</Button>
              </Box>

              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 'calc(100vh - 380px)' }}>
                <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { bgcolor: '#f3f4f6', fontWeight: 600 } }}>
                      <TableCell sx={{
                        position: 'sticky', left: 0, zIndex: 3, bgcolor: '#f3f4f6 !important',
                        width: 260, minWidth: 260, borderRight: '1px solid #e5e7eb',
                      }}>Parameter</TableCell>
                      <TableCell sx={{ width: 240 }}>Live Value</TableCell>
                      <TableCell sx={{ width: 130 }} align="center">Min</TableCell>
                      <TableCell sx={{ width: 130 }} align="center">Max</TableCell>
                      <TableCell sx={{ width: 120 }} align="center">
                        <Tooltip title="Only notify if the value stays out of range (below Min / above Max) continuously for at least this many seconds. Avoids alerting on brief spikes.">
                          <span>Duration (s)</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="center">
                        <Tooltip title="ON = require the breach to be sustained for the Duration before notifying. OFF = notify immediately on breach.">
                          <span>Sustain</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell sx={{ width: 90 }} align="center">Enabled</TableCell>
                      <TableCell sx={{ width: 50 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredNumeric.map(p => (
                      <TableRow key={p.key} hover sx={p.enabled ? { '& td': { bgcolor: '#fefce8' } } : {}}>
                        <TableCell sx={{
                          position: 'sticky', left: 0, zIndex: 2,
                          borderRight: '1px solid #e5e7eb', bgcolor: p.enabled ? '#fefce8' : '#fff',
                          width: 260, minWidth: 260, overflow: 'hidden',
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                            <code style={{ fontSize: 12, color: '#1f2937', fontWeight: 600, wordBreak: 'break-all' }}>{p.key}</code>
                            {p.custom && <Chip size="small" label="custom" sx={{ fontSize: 9, height: 16 }} />}
                          </Box>
                        </TableCell>
                        <TableCell sx={{
                          fontFamily: 'monospace', color: liveValues[p.key] !== undefined ? '#0f766e' : '#9ca3af',
                          fontSize: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240,
                        }}>
                          <Tooltip title={liveValues[p.key] !== undefined ? String(liveValues[p.key]) : 'no live value'}>
                            <span>{fmt(liveValues[p.key])}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            size="small" type="number" value={p.min}
                            onChange={(e) => updateParam(p.key, 'min', e.target.value)}
                            placeholder="—"
                            sx={{ width: 110, '& input': { padding: '6px 8px', fontSize: 13, textAlign: 'right', bgcolor: 'var(--y-bg, #fff)' } }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <TextField
                            size="small" type="number" value={p.max}
                            onChange={(e) => updateParam(p.key, 'max', e.target.value)}
                            placeholder="—"
                            sx={{ width: 110, '& input': { padding: '6px 8px', fontSize: 13, textAlign: 'right', bgcolor: 'var(--y-bg, #fff)' } }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={p.sustain ? '' : 'Turn on Sustain to use a duration'}>
                            <TextField
                              size="small" type="number" value={p.duration ?? ''}
                              onChange={(e) => updateParam(p.key, 'duration', e.target.value)}
                              placeholder="—"
                              disabled={!p.sustain}
                              InputProps={{ inputProps: { min: 0, step: 1 } }}
                              sx={{ width: 100, '& input': { padding: '6px 8px', fontSize: 13, textAlign: 'right', bgcolor: p.sustain ? '#fff' : '#f3f4f6' } }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">
                          <Switch size="small" checked={!!p.sustain}
                                  onChange={(e) => updateParam(p.key, 'sustain', e.target.checked)} />
                        </TableCell>
                        <TableCell align="center">
                          <Switch size="small" checked={p.enabled}
                                  onChange={(e) => updateParam(p.key, 'enabled', e.target.checked)} />
                        </TableCell>
                        <TableCell>
                          {p.custom && (
                            <Tooltip title="Remove">
                              <IconButton size="small" onClick={() => removeParam(p.key)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredNumeric.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                          {filter ? `No numeric params match "${filter}"` : 'No numeric parameters discovered. Add a custom param above.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* ──────────── TAB 1: ALARMS ──────────── */}
          {tab === 1 && (
            <Box sx={{ p: 2 }}>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: '#fef2f2', border: '1px solid #fecaca', borderRadius: 1 }}>
                <Typography sx={{ fontSize: 13, color: '#7f1d1d', fontWeight: 600 }}>
                  🚨 Alarm Triggers
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#991b1b' }}>
                  Every distinct alarm this machine has fired. Toggle the ones that should halt the machine. Use the time-window selector on the right to widen history.
                </Typography>
              </Box>

              {/* OBSERVED ALARMS (distinct alarm_number + alarm_message from history) */}
              <Paper variant="outlined" sx={{ mb: 3, borderColor: '#fca5a5' }}>
                <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#7f1d1d' }}>
                      📋 Observed Alarms on this Machine
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: '#991b1b' }}>
                      Distinct alarms recorded in the last {alarmHistoryDays} days. Enable the ones that should halt the machine.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>History window:</Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={alarmHistoryDays}
                      onChange={(_, v) => v && setAlarmHistoryDays(v)}
                      sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: 11, py: 0.2, px: 1 } }}
                    >
                      <ToggleButton value={1}>1d</ToggleButton>
                      <ToggleButton value={7}>7d</ToggleButton>
                      <ToggleButton value={30}>30d</ToggleButton>
                      <ToggleButton value={90}>90d</ToggleButton>
                    </ToggleButtonGroup>
                    <Chip
                      size="small"
                      label={`${observedAlarms.filter(a => a.enabled).length}/${observedAlarms.length} enabled`}
                      sx={{ bgcolor: 'var(--y-bg, #fff)', borderColor: '#fca5a5' }}
                      variant="outlined"
                    />
                  </Box>
                </Box>

                {observedAlarms.length > 0 && (
                  <Box sx={{ px: 1.5, py: 1, bgcolor: 'var(--y-bg, #fff)', borderBottom: '1px solid #fecaca', display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="🔍 Search alarms — by message text or alarm number (e.g. OVERTRAVEL, 500)"
                      value={alarmSearch}
                      onChange={(e) => setAlarmSearch(e.target.value)}
                      sx={{ '& input': { fontSize: 13 } }}
                      InputProps={{
                        endAdornment: alarmSearch ? (
                          <IconButton size="small" onClick={() => setAlarmSearch('')}>
                            <Delete fontSize="small" />
                          </IconButton>
                        ) : null,
                      }}
                    />
                    {alarmSearch && (
                      <Chip
                        size="small"
                        label={(() => {
                          const q = alarmSearch.toLowerCase();
                          const n = observedAlarms.filter(a =>
                            (a.message || '').toLowerCase().includes(q) ||
                            String(a.number ?? '').includes(q)
                          ).length;
                          return `${n} match${n === 1 ? '' : 'es'}`;
                        })()}
                        sx={{ bgcolor: '#fef2f2', color: '#b91c1c', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                )}

                {loadingAlarms ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
                ) : observedAlarms.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                    <Typography sx={{ fontSize: 13 }}>No alarms observed on this machine in the last {alarmHistoryDays} days.</Typography>
                    <Typography sx={{ fontSize: 11, mt: 0.5 }}>Try widening the window above.</Typography>
                  </Box>
                ) : (
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#fef2f2', fontWeight: 700, fontSize: 12, color: '#7f1d1d' } }}>
                          <TableCell sx={{ width: 90 }}>Alarm #</TableCell>
                          <TableCell>Alarm Message</TableCell>
                          <TableCell sx={{ width: 90 }} align="center">Times Seen</TableCell>
                          <TableCell sx={{ width: 150 }}>Last Seen</TableCell>
                          <TableCell sx={{ width: 100 }} align="center">Halt machine?</TableCell>
                          <TableCell sx={{ width: 100 }} align="center">Interlock</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(() => {
                          const q = alarmSearch.trim().toLowerCase();
                          const matchedIdx = observedAlarms
                            .map((a, i) => ({ a, i }))
                            .filter(({ a }) => !q || (a.message || '').toLowerCase().includes(q) || String(a.number ?? '').includes(q));
                          if (q && matchedIdx.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                  No alarms match "<b>{alarmSearch}</b>".
                                  <br />
                                  <Typography sx={{ fontSize: 11, mt: 0.5 }}>
                                    Try a shorter search term, or widen the history window.
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            );
                          }
                          return matchedIdx.map(({ a, i: idx }) => (
                          <TableRow
                            key={`${a.number ?? ''}|${a.message ?? ''}`}
                            hover
                            sx={a.enabled ? { '& td': { bgcolor: '#fee2e2' } } : {}}
                          >
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: a.number ? '#b91c1c' : '#9ca3af' }}>
                              {a.number ?? '—'}
                            </TableCell>
                            <TableCell sx={{ fontSize: 13 }}>
                              <Tooltip title={a.message || '(no message)'}>
                                <span>{a.message || <em style={{ color: '#9ca3af' }}>(no message)</em>}</span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center" sx={{ fontSize: 12 }}>
                              {a.count > 0 ? (
                                <Chip size="small" label={a.count} sx={{ bgcolor: '#fef2f2', color: '#b91c1c', fontWeight: 600, height: 18 }} />
                              ) : (
                                <Tooltip title="saved rule — not seen in current window">
                                  <Chip size="small" label="—" variant="outlined" sx={{ fontSize: 10, height: 18, color: '#9ca3af' }} />
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                              {a.lastSeen ? new Date(a.lastSeen).toLocaleString() : '—'}
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title={a.halt ? 'Hard interlock (halt machine)' : 'Soft alert only (notify but do not halt)'}>
                                <span>
                                  <Switch
                                    size="small"
                                    checked={!!a.halt}
                                    onChange={(e) => toggleObservedAlarm(idx, 'halt', e.target.checked)}
                                    disabled={!a.enabled}
                                    color="error"
                                  />
                                </span>
                              </Tooltip>
                            </TableCell>
                            <TableCell align="center">
                              <Switch
                                checked={!!a.enabled}
                                onChange={(e) => toggleObservedAlarm(idx, 'enabled', e.target.checked)}
                                color="error"
                              />
                            </TableCell>
                          </TableRow>
                        ));
                        })()}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>

            </Box>
          )}

          {/* ──────────── TAB 2: JIDOKA ──────────── */}
          {tab === 2 && (
            <Box sx={{ p: 2 }}>
              <Paper variant="outlined" sx={{ p: 2.5, bgcolor: jidoka.enabled ? '#fef2f2' : '#f9fafb',
                       borderColor: jidoka.enabled ? '#fca5a5' : '#e5e7eb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: jidoka.enabled ? '#991b1b' : '#374151' }}>
                      自働化 Jidoka — Autonomation
                    </Typography>
                    <Tooltip title="Toyota Production System: machine auto-stops on detected abnormality, operator can pull andon cord, supervisor ack required to resume, root cause logged.">
                      <Chip size="small" label="?" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                    </Tooltip>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={jidoka.enabled}
                        onChange={(e) => setJidoka({ ...jidoka, enabled: e.target.checked })}
                        color="error"
                      />
                    }
                    label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>{jidoka.enabled ? 'ENABLED' : 'Disabled'}</Typography>}
                    sx={{ m: 0 }}
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1, fontWeight: 600 }}>STOP CONDITIONS</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, mb: 2, opacity: jidoka.enabled ? 1 : 0.55 }}>
                  <TextField
                    size="small" type="number" label="Stop after N consecutive bad parts"
                    value={jidoka.stop_on_consecutive_bad_parts}
                    onChange={(e) => setJidoka({ ...jidoka, stop_on_consecutive_bad_parts: Number(e.target.value) || 0 })}
                    disabled={!jidoka.enabled}
                    InputProps={{ inputProps: { min: 0, step: 1 } }}
                    helperText="Halt when this many defective parts in a row"
                  />
                  <TextField
                    size="small" type="number" label="Cycle-time deviation alert (%)"
                    value={jidoka.cycle_time_deviation_pct}
                    onChange={(e) => setJidoka({ ...jidoka, cycle_time_deviation_pct: Number(e.target.value) || 0 })}
                    disabled={!jidoka.enabled}
                    InputProps={{ inputProps: { min: 0, max: 100, step: 1 } }}
                    helperText="Actual > standard × (1 + N%) → alert"
                  />
                  <TextField
                    size="small" type="number" label="Idle alert (seconds)"
                    value={jidoka.idle_alert_seconds}
                    onChange={(e) => setJidoka({ ...jidoka, idle_alert_seconds: Number(e.target.value) || 0 })}
                    disabled={!jidoka.enabled}
                    InputProps={{ inputProps: { min: 0, step: 30 } }}
                    helperText="Halt if idle longer than this"
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1, fontWeight: 600 }}>OPERATOR & SUPERVISOR</Typography>
                <Box sx={{ display: 'flex', gap: 3, mb: 2, flexWrap: 'wrap', opacity: jidoka.enabled ? 1 : 0.55 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={jidoka.andon_cord_enabled}
                        onChange={(e) => setJidoka({ ...jidoka, andon_cord_enabled: e.target.checked })}
                        disabled={!jidoka.enabled}
                        color="warning"
                      />
                    }
                    label={<Typography sx={{ fontSize: 13 }}>🟡 Operator andon cord (manual halt button on operator screen)</Typography>}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={jidoka.require_supervisor_ack}
                        onChange={(e) => setJidoka({ ...jidoka, require_supervisor_ack: e.target.checked })}
                        disabled={!jidoka.enabled}
                        color="error"
                      />
                    }
                    label={<Typography sx={{ fontSize: 13 }}>🔴 Supervisor acknowledgement required to resume</Typography>}
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1, fontWeight: 600 }}>ROOT-CAUSE CATEGORIES</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>
                  Operator must pick one of these before resuming after a halt.
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', opacity: jidoka.enabled ? 1 : 0.55 }}>
                  {(jidoka.root_cause_categories || []).map(c => (
                    <Chip
                      key={c} label={c} size="small"
                      onDelete={jidoka.enabled ? () => setJidoka({ ...jidoka, root_cause_categories: jidoka.root_cause_categories.filter(x => x !== c) }) : undefined}
                      sx={{ bgcolor: 'var(--y-bg, #fff)', borderColor: '#fca5a5' }}
                      variant="outlined"
                    />
                  ))}
                  <TextField
                    size="small" placeholder="+ add cause"
                    value={jidokaCauseDraft}
                    onChange={(e) => setJidokaCauseDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && jidokaCauseDraft.trim() && jidoka.enabled) {
                        const k = jidokaCauseDraft.trim();
                        if (!(jidoka.root_cause_categories || []).includes(k)) {
                          setJidoka({ ...jidoka, root_cause_categories: [...(jidoka.root_cause_categories || []), k] });
                        }
                        setJidokaCauseDraft('');
                      }
                    }}
                    disabled={!jidoka.enabled}
                    sx={{ width: 160, '& input': { padding: '4px 8px', fontSize: 12 } }}
                  />
                </Box>
              </Paper>

              {/* ── PATTERN RULES (Jidoka orchestration of numeric+alarm) ───── */}
              <Paper variant="outlined" sx={{ mt: 2, p: 2.5, bgcolor: jidoka.enabled ? '#fff7ed' : '#f9fafb', borderColor: jidoka.enabled ? '#fdba74' : '#e5e7eb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: jidoka.enabled ? '#9a3412' : '#374151' }}>
                      🧩 Pattern Rules
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                      Compose your enabled numeric thresholds + alarm triggers into smarter halts. Catches patterns one event alone wouldn't catch.
                    </Typography>
                  </Box>
                  <FormControl size="small" sx={{ minWidth: 200 }} disabled={!jidoka.enabled}>
                    <InputLabel>+ Add pattern</InputLabel>
                    <Select
                      value=""
                      label="+ Add pattern"
                      onChange={(e) => {
                        if (!e.target.value) return;
                        setJidoka({ ...jidoka, patterns: [...(jidoka.patterns || []), newPattern(e.target.value)] });
                      }}
                    >
                      {PATTERN_TYPES.map(t => (
                        <MenuItem key={t.value} value={t.value}>
                          <Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{t.label}</Typography>
                            <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>{t.desc}</Typography>
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>

                {(!jidoka.patterns || jidoka.patterns.length === 0) ? (
                  <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary', bgcolor: 'var(--y-bg, #fff)', borderRadius: 1, border: '1px dashed #d1d5db' }}>
                    <Typography sx={{ fontSize: 13 }}>No patterns yet.</Typography>
                    <Typography sx={{ fontSize: 11, mt: 0.5 }}>
                      Example: "Same alarm 3× in 15 min → halt + supervisor ack" — catches recurring problems that single events miss.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, opacity: jidoka.enabled ? 1 : 0.55 }}>
                    {jidoka.patterns.map((p, idx) => {
                      const def = PATTERN_TYPES.find(t => t.value === p.type) || PATTERN_TYPES[0];
                      const updateP = (field, value) => {
                        const next = [...jidoka.patterns];
                        next[idx] = { ...next[idx], [field]: value };
                        setJidoka({ ...jidoka, patterns: next });
                      };
                      const updateAction = (field, value) => {
                        const next = [...jidoka.patterns];
                        next[idx] = { ...next[idx], action: { ...(next[idx].action || {}), [field]: value } };
                        setJidoka({ ...jidoka, patterns: next });
                      };
                      const removeP = () => {
                        setJidoka({ ...jidoka, patterns: jidoka.patterns.filter((_, i) => i !== idx) });
                      };
                      return (
                        <Paper key={idx} variant="outlined" sx={{ p: 2, borderLeft: p.enabled ? '4px solid #ea580c' : '4px solid #fed7aa', bgcolor: 'var(--y-bg, #fff)' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                            <TextField
                              size="small"
                              value={p.name || ''}
                              onChange={(e) => updateP('name', e.target.value)}
                              placeholder="Pattern name"
                              disabled={!jidoka.enabled}
                              sx={{ flex: '1 1 220px', '& input': { fontWeight: 600, fontSize: 13 } }}
                            />
                            <Chip size="small" label={def.label} sx={{ bgcolor: '#fff7ed', color: '#9a3412', fontWeight: 600 }} />
                            <Tooltip title={p.enabled ? 'Pattern is active' : 'Pattern is paused'}>
                              <Switch size="small" checked={!!p.enabled} onChange={(e) => updateP('enabled', e.target.checked)} disabled={!jidoka.enabled} color="warning" />
                            </Tooltip>
                            <IconButton size="small" onClick={removeP} disabled={!jidoka.enabled}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary', mb: 1 }}>{def.desc}</Typography>

                          {/* Type-specific fields */}
                          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 1, mb: 1.5 }}>
                            {def.fields.includes('min_count') && (
                              <TextField size="small" type="number" label="Count threshold" value={p.min_count ?? ''} onChange={(e) => updateP('min_count', Number(e.target.value) || 0)} disabled={!jidoka.enabled} InputProps={{ inputProps: { min: 1, step: 1 } }} />
                            )}
                            {def.fields.includes('window_minutes') && (
                              <TextField size="small" type="number" label="Time window (minutes)" value={p.window_minutes ?? ''} onChange={(e) => updateP('window_minutes', Number(e.target.value) || 0)} disabled={!jidoka.enabled} InputProps={{ inputProps: { min: 1, step: 1 } }} />
                            )}
                            {def.fields.includes('alarm_filter') && (
                              <FormControl size="small" disabled={!jidoka.enabled}>
                                <InputLabel>Alarm match</InputLabel>
                                <Select value={p.alarm_filter || 'any'} label="Alarm match" onChange={(e) => updateP('alarm_filter', e.target.value)}>
                                  <MenuItem value="any">Any enabled alarm</MenuItem>
                                  <MenuItem value="same_number">Same alarm number</MenuItem>
                                  <MenuItem value="same_message">Same alarm message</MenuItem>
                                </Select>
                              </FormControl>
                            )}
                            {def.fields.includes('first_alarm') && (
                              <TextField size="small" label="First alarm (number or message)" value={p.first_alarm || ''} onChange={(e) => updateP('first_alarm', e.target.value)} disabled={!jidoka.enabled} placeholder="e.g. 500 or OVERTRAVEL" />
                            )}
                            {def.fields.includes('second_alarm') && (
                              <TextField size="small" label="Second alarm" value={p.second_alarm || ''} onChange={(e) => updateP('second_alarm', e.target.value)} disabled={!jidoka.enabled} placeholder="e.g. HYDRAULIC" />
                            )}
                          </Box>

                          {/* Action row */}
                          <Divider sx={{ my: 1 }} />
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>WHEN PATTERN MATCHES →</Typography>
                            <FormControlLabel
                              control={<Switch size="small" checked={p.action?.halt ?? true} onChange={(e) => updateAction('halt', e.target.checked)} disabled={!jidoka.enabled} color="error" />}
                              label={<Typography sx={{ fontSize: 12 }}>🔴 Halt machine</Typography>}
                            />
                            <FormControlLabel
                              control={<Switch size="small" checked={p.action?.require_supervisor_ack ?? true} onChange={(e) => updateAction('require_supervisor_ack', e.target.checked)} disabled={!jidoka.enabled} color="error" />}
                              label={<Typography sx={{ fontSize: 12 }}>👤 Require supervisor ack</Typography>}
                            />
                          </Box>
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Box>
          )}

          {/* ──────────── TAB 3: TOOL LIFE ──────────── */}
          {tab === 3 && (
            <Box sx={{ p: 2 }}>
              <Paper variant="outlined" sx={{ p: 2.5, mb: 2, bgcolor: toolLife.enabled ? '#fff7ed' : '#f9fafb', borderColor: toolLife.enabled ? '#fdba74' : '#e5e7eb' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: toolLife.enabled ? '#9a3412' : '#374151' }}>
                      🔧 Tool Life Management
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                      Define expected life per tool (cutting minutes + part count). Edge tracks usage, warns at threshold, halts at end-of-life.
                    </Typography>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={toolLife.enabled}
                        onChange={(e) => setToolLife({ ...toolLife, enabled: e.target.checked })}
                        color="primary"
                      />
                    }
                    label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>{toolLife.enabled ? 'ENABLED' : 'Disabled'}</Typography>}
                    sx={{ m: 0 }}
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                {/* Live current tool indicator */}
                {liveValues.live_tool_number !== undefined && (
                  <Box sx={{ p: 1.5, mb: 2, bgcolor: 'var(--y-bg, #fff)', borderRadius: 1, border: '1px solid #93c5fd', display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 11, color: 'text.secondary', fontWeight: 600 }}>CURRENT ACTIVE TOOL</Typography>
                    <Chip
                      label={`T${String(liveValues.live_tool_number).padStart(2, '0')}`}
                      sx={{ bgcolor: '#1e40af', color: '#fff', fontWeight: 700, fontSize: 14, height: 28 }}
                    />
                    {(() => {
                      const num = Number(liveValues.live_tool_number);
                      const cfg = (toolLife.tools || []).find(t => Number(t.tool_number) === num);
                      if (!cfg) {
                        return <Typography sx={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>not configured below — add it for monitoring</Typography>;
                      }
                      return <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{cfg.name || '(unnamed)'}</Typography>;
                    })()}
                  </Box>
                )}

                {/* Tool config table */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, opacity: toolLife.enabled ? 1 : 0.6 }}>
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', fontWeight: 600 }}>CONFIGURED TOOLS</Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    disabled={!toolLife.enabled}
                    onClick={() => {
                      const existing = (toolLife.tools || []).map(t => Number(t.tool_number));
                      let next = 1;
                      while (existing.includes(next)) next++;
                      setToolLife({ ...toolLife, tools: [...(toolLife.tools || []), newTool(next)] });
                    }}
                  >
                    Add Tool
                  </Button>
                </Box>

                {(!toolLife.tools || toolLife.tools.length === 0) ? (
                  <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary', bgcolor: 'var(--y-bg, #fff)', borderRadius: 1, border: '1px dashed #d1d5db' }}>
                    <Typography sx={{ fontSize: 13 }}>No tools configured yet.</Typography>
                    <Typography sx={{ fontSize: 11, mt: 0.5 }}>
                      Click "Add Tool" to define a tool's expected life (cutting minutes + parts).
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ '& th': { bgcolor: '#eff6ff', fontWeight: 600, fontSize: 12, color: '#1e3a8a' } }}>
                          <TableCell sx={{ width: 80 }}>Tool #</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell sx={{ width: 130 }} align="center">Life (minutes)</TableCell>
                          <TableCell sx={{ width: 130 }} align="center">Life (parts)</TableCell>
                          <TableCell sx={{ width: 110 }} align="center">Warn at %</TableCell>
                          <TableCell sx={{ width: 110 }} align="center">Halt at %</TableCell>
                          <TableCell sx={{ width: 90 }} align="center">Halt?</TableCell>
                          <TableCell sx={{ width: 90 }} align="center">Active</TableCell>
                          <TableCell sx={{ width: 50 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(toolLife.tools || []).map((t, idx) => {
                          const isActive = liveValues.live_tool_number !== undefined && Number(liveValues.live_tool_number) === Number(t.tool_number);
                          const updateTool = (field, value) => {
                            const next = [...toolLife.tools];
                            next[idx] = { ...next[idx], [field]: value };
                            setToolLife({ ...toolLife, tools: next });
                          };
                          const removeTool = () => {
                            setToolLife({ ...toolLife, tools: toolLife.tools.filter((_, i) => i !== idx) });
                          };
                          return (
                            <TableRow key={idx} sx={isActive ? { '& td': { bgcolor: '#fef9c3', borderLeft: '3px solid #ca8a04' } } : t.enabled ? { '& td': { bgcolor: 'var(--y-bg, #fff)' } } : { '& td': { bgcolor: '#f9fafb', opacity: 0.7 } }}>
                              <TableCell>
                                <TextField
                                  size="small" type="number"
                                  value={t.tool_number ?? ''}
                                  onChange={(e) => updateTool('tool_number', Number(e.target.value) || 0)}
                                  disabled={!toolLife.enabled}
                                  InputProps={{ inputProps: { min: 1, step: 1, style: { fontWeight: 700, textAlign: 'center', padding: '6px' } } }}
                                  sx={{ width: 70 }}
                                />
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small" fullWidth
                                  placeholder="e.g. Drill 6mm"
                                  value={t.name || ''}
                                  onChange={(e) => updateTool('name', e.target.value)}
                                  disabled={!toolLife.enabled}
                                  sx={{ '& input': { fontSize: 13 } }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small" type="number"
                                  value={t.life_minutes ?? ''}
                                  onChange={(e) => updateTool('life_minutes', Number(e.target.value) || 0)}
                                  disabled={!toolLife.enabled}
                                  InputProps={{ inputProps: { min: 0, step: 10 } }}
                                  sx={{ width: 110 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small" type="number"
                                  value={t.life_parts ?? ''}
                                  onChange={(e) => updateTool('life_parts', Number(e.target.value) || 0)}
                                  disabled={!toolLife.enabled}
                                  InputProps={{ inputProps: { min: 0, step: 10 } }}
                                  sx={{ width: 110 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small" type="number"
                                  value={t.warning_pct ?? 80}
                                  onChange={(e) => updateTool('warning_pct', Number(e.target.value) || 0)}
                                  disabled={!toolLife.enabled}
                                  InputProps={{ inputProps: { min: 0, max: 100, step: 5 } }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <TextField
                                  size="small" type="number"
                                  value={t.halt_pct ?? 100}
                                  onChange={(e) => updateTool('halt_pct', Number(e.target.value) || 0)}
                                  disabled={!toolLife.enabled}
                                  InputProps={{ inputProps: { min: 0, max: 200, step: 5 } }}
                                  sx={{ width: 80 }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip title={t.halt_machine ? 'Hard halt at end-of-life' : 'Soft alert only (do not halt)'}>
                                  <Switch
                                    size="small"
                                    checked={!!t.halt_machine}
                                    onChange={(e) => updateTool('halt_machine', e.target.checked)}
                                    disabled={!toolLife.enabled}
                                    color="error"
                                  />
                                </Tooltip>
                              </TableCell>
                              <TableCell align="center">
                                <Switch
                                  size="small"
                                  checked={!!t.enabled}
                                  onChange={(e) => updateTool('enabled', e.target.checked)}
                                  disabled={!toolLife.enabled}
                                />
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: 'flex', gap: 0.5 }}>
                                  <Tooltip title={t.wear_curve?.enabled
                                    ? `Wear comp ON — ${t.wear_curve.rate_um_per_100}µm/100 parts (${t.wear_curve.axis})`
                                    : 'Configure wear compensation (suggests tool offset as tool wears)'}>
                                    <IconButton size="small" onClick={() => { setWearDialogIdx(idx); setWearDraft({ ...(t.wear_curve || { enabled: false, rate_um_per_100: 5, axis: 'radial', notes: '' }) }); }} disabled={!toolLife.enabled}>
                                      <Typography sx={{ fontSize: 16, color: t.wear_curve?.enabled ? '#ea580c' : '#9ca3af' }}>💡</Typography>
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Remove this tool">
                                    <IconButton size="small" onClick={removeTool} disabled={!toolLife.enabled}>
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

              </Paper>
            </Box>
          )}
        </Paper>
      )}

      {/* ── WEAR COMPENSATION DIALOG ──────────────────────────────────── */}
      <Dialog open={wearDialogIdx !== null} onClose={() => setWearDialogIdx(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: '#fff7ed', borderBottom: '1px solid #fed7aa' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ fontSize: 18 }}>💡</Typography>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#9a3412' }}>Wear Compensation</Typography>
              <Typography sx={{ fontSize: 11, color: '#6b7280' }}>
                Predict the tool offset adjustment as this tool wears. Phase A: shows a suggestion to the operator. Phase B (later): auto-apply with approval.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {wearDraft && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!wearDraft.enabled}
                    onChange={(e) => setWearDraft({ ...wearDraft, enabled: e.target.checked })}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#ea580c' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#ea580c' },
                    }}
                  />
                }
                label={<Typography sx={{ fontSize: 14, fontWeight: 600 }}>{wearDraft.enabled ? 'Compensation enabled' : 'Disabled — no suggestions shown'}</Typography>}
              />
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, opacity: wearDraft.enabled ? 1 : 0.55 }}>
                <TextField
                  type="number"
                  label="Wear rate (µm per 100 parts)"
                  value={wearDraft.rate_um_per_100 ?? ''}
                  onChange={(e) => setWearDraft({ ...wearDraft, rate_um_per_100: Number(e.target.value) || 0 })}
                  disabled={!wearDraft.enabled}
                  helperText="Typical: 3-15 µm/100 parts depending on material + tool"
                  InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                />
                <FormControl disabled={!wearDraft.enabled}>
                  <InputLabel>Wear axis</InputLabel>
                  <Select
                    label="Wear axis"
                    value={wearDraft.axis || 'radial'}
                    onChange={(e) => setWearDraft({ ...wearDraft, axis: e.target.value })}
                  >
                    <MenuItem value="radial">Radial (diameter — D offset)</MenuItem>
                    <MenuItem value="axial">Axial (length — H offset)</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <TextField
                multiline
                rows={2}
                label="Notes (material, conditions, prior corrections)"
                value={wearDraft.notes || ''}
                onChange={(e) => setWearDraft({ ...wearDraft, notes: e.target.value })}
                disabled={!wearDraft.enabled}
                placeholder="e.g. SS304, 3000 RPM, coolant on. Last offset applied: -8µm at 70% life."
              />
              <Box sx={{ bgcolor: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 1, p: 1.5 }}>
                <Typography sx={{ fontSize: 12, color: '#92400e', fontWeight: 600 }}>📊 Suggestion preview</Typography>
                <Typography sx={{ fontSize: 12, color: '#92400e', mt: 0.5 }}>
                  Example: after 200 parts, the suggested {wearDraft.axis} offset will be{' '}
                  <b>{((200 / 100) * (wearDraft.rate_um_per_100 || 0)).toFixed(1)} µm</b>
                  {' '}({((200 / 100) * (wearDraft.rate_um_per_100 || 0) / 1000).toFixed(4)} mm).
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#92400e', mt: 0.5, fontStyle: 'italic' }}>
                  Phase B will allow the operator to apply this offset to the machine with one click (or auto-apply with approval). Today, it's an informational suggestion only.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: '#f8fafc', borderTop: '1px solid #e5e7eb' }}>
          <Button onClick={() => setWearDialogIdx(null)} sx={{ color: '#6b7280' }}>Cancel</Button>
          <Button
            variant="contained"
            sx={{ bgcolor: '#2563eb', '&:hover': { bgcolor: '#1d4ed8' } }}
            onClick={() => {
              const next = [...toolLife.tools];
              next[wearDialogIdx] = { ...next[wearDialogIdx], wear_curve: wearDraft };
              setToolLife({ ...toolLife, tools: next });
              setWearDialogIdx(null);
            }}
          >
            Apply (remember to Save the page)
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InterlockSettings;
